import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../lib/formatters';

const TransactionSummary = ({ transactions, deal, typeFilter, setTypeFilter }) => {
  const transactionCounts = useMemo(() => {
    const active = transactions.filter(t => t.status !== 'void');
    const payments = active.filter(t => t.type === 'PAYMENT' && t.cleared_payment_eligible === true);
    const bounces = active.filter(t => t.type === 'ACH_RETURN');
    const fees = active.filter(t => ['BOUNCE_FEE', 'DEFAULT_FEE', 'DEFERRED_FEE', 'CUSTOM_FEE'].includes(t.type));
    const earlyPayoffs = active.filter(t => t.type === 'EARLY_PAYOFF');
    const refiPayoffs = active.filter(t => t.type === 'REFINANCE_PAYOFF');
    return {
      payments: payments.length,
      paymentsTotal: payments.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0),
      bounces: bounces.length,
      bouncesTotal: bounces.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0),
      fees: fees.length,
      feesTotal: fees.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0),
      earlyPayoffs: earlyPayoffs.length,
      earlyPayoffsTotal: earlyPayoffs.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0),
      refiPayoffs: refiPayoffs.length,
      refiPayoffsTotal: refiPayoffs.reduce((sum, t) => sum + Math.abs(t.amount || 0), 0),
    };
  }, [transactions]);

  // Source-side deferred-fee aggregation. One pill per distinct target deal.
  // The backend `GET /api/deals/{id}` rolls up FEE_DEFERRED_OUT transactions
  // into `fees_deferred_out: [{target_deal_id, target_deal_id_number,
  // total_amount, transaction_count}]`. Voided rows are pre-filtered server-side.
  const feesDeferredOut = Array.isArray(deal?.fees_deferred_out) ? deal.fees_deferred_out : [];

  return (
    <div className="flex flex-wrap gap-3 mt-2 text-sm">
      <button
        type="button"
        data-testid="filter-payments"
        onClick={() => setTypeFilter(f => f === 'payments' ? null : 'payments')}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all cursor-pointer ${typeFilter === 'payments' ? 'bg-green-600 text-white ring-2 ring-green-400' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
      >
        <span className="font-semibold">{transactionCounts.payments}</span> Payments
        <span className={typeFilter === 'payments' ? 'text-green-200' : 'text-green-600'}>({formatCurrency(transactionCounts.paymentsTotal)})</span>
      </button>
      <button
        type="button"
        data-testid="filter-bounces"
        onClick={() => setTypeFilter(f => f === 'bounces' ? null : 'bounces')}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all cursor-pointer ${typeFilter === 'bounces' ? 'bg-red-600 text-white ring-2 ring-red-400' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
      >
        <span className="font-semibold">{transactionCounts.bounces}</span> Bounces
        <span className={typeFilter === 'bounces' ? 'text-red-200' : 'text-red-600'}>({formatCurrency(transactionCounts.bouncesTotal)})</span>
      </button>
      <button
        type="button"
        data-testid="filter-fees"
        onClick={() => setTypeFilter(f => f === 'fees' ? null : 'fees')}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all cursor-pointer ${typeFilter === 'fees' ? 'bg-amber-600 text-white ring-2 ring-amber-400' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
      >
        <span className="font-semibold">{transactionCounts.fees}</span> Fees
        <span className={typeFilter === 'fees' ? 'text-amber-200' : 'text-amber-600'}>({formatCurrency(transactionCounts.feesTotal)})</span>
      </button>
      {transactionCounts.earlyPayoffs > 0 && (
        <button
          type="button"
          data-testid="filter-early-payoffs"
          onClick={() => setTypeFilter(f => f === 'early_payoffs' ? null : 'early_payoffs')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all cursor-pointer ${typeFilter === 'early_payoffs' ? 'bg-cyan-600 text-white ring-2 ring-cyan-400' : 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200'}`}
        >
          <span className="font-semibold">{transactionCounts.earlyPayoffs}</span> Early Payoffs
          <span className={typeFilter === 'early_payoffs' ? 'text-cyan-200' : 'text-cyan-600'}>({formatCurrency(transactionCounts.earlyPayoffsTotal)})</span>
        </button>
      )}
      {transactionCounts.refiPayoffs > 0 && (
        <button
          type="button"
          data-testid="filter-refi-payoffs"
          onClick={() => setTypeFilter(f => f === 'refi_payoffs' ? null : 'refi_payoffs')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all cursor-pointer ${typeFilter === 'refi_payoffs' ? 'bg-indigo-600 text-white ring-2 ring-indigo-400' : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'}`}
        >
          <span className="font-semibold">{transactionCounts.refiPayoffs}</span> Refi Payoffs
          <span className={typeFilter === 'refi_payoffs' ? 'text-indigo-200' : 'text-indigo-600'}>({formatCurrency(transactionCounts.refiPayoffsTotal)})</span>
        </button>
      )}
      {feesDeferredOut.map((fdo) => (
        <span
          key={fdo.target_deal_id}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-100 text-orange-900 border border-orange-300"
          title={`${fdo.transaction_count} transfer${fdo.transaction_count !== 1 ? 's' : ''} to deal #${fdo.target_deal_id_number}`}
          data-testid={`fees-deferred-out-pill-${fdo.target_deal_id_number}`}
        >
          <span className="text-[10px] uppercase tracking-wider font-semibold">Fees Deferred</span>
          <span className="font-semibold">−{formatCurrency(fdo.total_amount)}</span>
          <span className="text-orange-700 text-xs">to</span>
          <Link
            to={`/deals/${fdo.target_deal_id_number}`}
            className="text-orange-800 underline underline-offset-2 hover:text-orange-950 font-medium"
          >
            #{fdo.target_deal_id_number}
          </Link>
        </span>
      ))}
      {typeFilter && (
        <button
          type="button"
          data-testid="filter-clear"
          onClick={() => setTypeFilter(null)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 cursor-pointer text-xs"
        >
          Clear filter
        </button>
      )}
    </div>
  );
};

export default TransactionSummary;
